import { FC, ReactNode, useState, useRef, useCallback, useMemo } from 'react';
import { SidebarControlContext, SidebarSelectionCallback } from './SidebarControlContext';

export type SidebarView = 'entity' | 'timeline' | 'reminders' | 'log' | 'fields';

interface SidebarControlProviderProps {
  children: ReactNode;
  setIsSidebarOpen: (open: boolean) => void;
  setActiveView: (view: SidebarView) => void;
}

/**
 * Provider for sidebar control functionality
 * Owns all sidebar entity selection state for fully controlled components
 * Supports callback pattern to notify opener of selection changes
 *
 * `newRecordId` is part of that state rather than local to the sidebar body: a
 * record created outside the sidebar (ReferencedListUI creating a referencing
 * record) must open unlocked and namable, and the opener is the only one that
 * knows it is new. It holds the record id, not a flag, so no other selection can
 * inherit it, and `closeSidebar` clears it — the sidebar body used to lose the
 * flag with its own `destroyOnHidden` unmount, and the mark must die with the
 * same session.
 *
 * `switchToManualEntry` is state, not a ref like `onSelectionChangeRef`: the
 * entity tab renders its "Switch to Manual Entry" control from it, and a ref
 * would not re-render. It is stored through the functional-update form, because
 * `useState` would otherwise call the callback instead of holding it.
 */
export const SidebarControlProvider: FC<SidebarControlProviderProps> = ({
  children,
  setIsSidebarOpen,
  setActiveView,
}) => {
  const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>();
  const [selectedRecordId, setSelectedRecordId] = useState<string | undefined>();
  const [selectedRecordName, setSelectedRecordName] = useState<string | undefined>();
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();
  const [newRecordId, setNewRecordId] = useState<string | undefined>();
  const [switchToManualEntry, setSwitchToManualEntry] = useState<(() => void) | undefined>();

  const onSelectionChangeRef = useRef<SidebarSelectionCallback | undefined>(undefined);

  const openEntitySidebar = useCallback((
    entityId: string,
    recordId?: string,
    entityGroupId?: string,
    recordName?: string,
    onSelectionChange?: SidebarSelectionCallback,
    recordIsNew?: boolean,
    onSwitchToManualEntry?: () => void
  ) => {
    setSelectedEntityId(entityId);
    setSelectedRecordId(recordId);
    setSelectedGroupId(entityGroupId);
    setSelectedRecordName(recordName);
    setNewRecordId(recordIsNew ? recordId : undefined);
    setSwitchToManualEntry(() => onSwitchToManualEntry);

    onSelectionChangeRef.current = onSelectionChange;

    setActiveView('entity');
    setIsSidebarOpen(true);
  }, [setActiveView, setIsSidebarOpen]);

  const openRemindersSidebar = useCallback(() => {
    setActiveView('reminders');
    setIsSidebarOpen(true);
  }, [setActiveView, setIsSidebarOpen]);

  const openTimelineSidebar = useCallback(() => {
    setActiveView('timeline');
    setIsSidebarOpen(true);
  }, [setActiveView, setIsSidebarOpen]);

  const hasBinding = useCallback(() => {
    return !!onSelectionChangeRef.current;
  }, []);

  const clearBinding = useCallback(() => {
    onSelectionChangeRef.current = undefined;
  }, []);

  const closeSidebar = useCallback(() => {
    const callback = onSelectionChangeRef.current;

    onSelectionChangeRef.current = undefined;
    setNewRecordId(undefined);
    setSwitchToManualEntry(undefined);
    setIsSidebarOpen(false);

    if (callback && selectedEntityId) {
      requestAnimationFrame(() => {
        callback(selectedEntityId, selectedRecordId, selectedGroupId, selectedRecordName);
      });
    }
  }, [setIsSidebarOpen, selectedEntityId, selectedRecordId, selectedGroupId, selectedRecordName]);

  const contextValue = useMemo(() => ({
    openEntitySidebar,
    openRemindersSidebar,
    openTimelineSidebar,
    closeSidebar,
    hasBinding,
    clearBinding,
    selectedEntityId,
    selectedRecordId,
    selectedRecordName,
    selectedGroupId,
    newRecordId,
    switchToManualEntry,
    setSelectedEntityId,
    setSelectedRecordId,
    setSelectedRecordName,
    setSelectedGroupId,
    setNewRecordId,
  }), [
    openEntitySidebar,
    openRemindersSidebar,
    openTimelineSidebar,
    closeSidebar,
    hasBinding,
    clearBinding,
    selectedEntityId,
    selectedRecordId,
    selectedRecordName,
    selectedGroupId,
    newRecordId,
    switchToManualEntry,
  ]);

  return (
    <SidebarControlContext.Provider value={contextValue}>
      {children}
    </SidebarControlContext.Provider>
  );
};

